import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { openRequestAccess } from "@/components/landing/RequestAccessDialog";

/**
 * Sticky mobile CTA that surfaces after the user scrolls ~40% of the page.
 * Marketing pages only. Dismissible per-session. Honors safe-area-inset.
 */
const StickyMobileCTA = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Only on top-level marketing surfaces
  const isMarketing =
    location.pathname === "/" ||
    location.pathname.startsWith("/blog") ||
    location.pathname.startsWith("/ms-") ||
    location.pathname.startsWith("/living-with-ms");

  useEffect(() => {
    if (!isMarketing) return;
    if (sessionStorage.getItem("sticky-cta-dismissed") === "1") {
      setDismissed(true);
      return;
    }
    const onScroll = () => {
      const doc = document.documentElement;
      const pct = (window.scrollY + window.innerHeight) / doc.scrollHeight;
      setVisible(pct > 0.4);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMarketing, location.pathname]);

  if (!isMarketing || dismissed || !visible) return null;

  return (
    <div
      role="complementary"
      aria-label="Quick start"
      className="md:hidden fixed inset-x-0 bottom-0 z-40 px-3 pointer-events-none"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="pointer-events-auto rounded-full bg-primary text-primary-foreground shadow-2xl shadow-primary/30 flex items-center gap-2 pl-4 pr-1 py-1 backdrop-blur">
        <button
          type="button"
          className="flex-1 flex items-center justify-between gap-3 min-h-11 text-left"
          onClick={() => openRequestAccess("sticky_mobile")}
        >
          <span className="text-[13px] font-medium tracking-tight">
            Request limited pilot access
          </span>
          <span className="inline-flex items-center justify-center bg-primary-foreground text-primary rounded-full w-9 h-9">
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </span>
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          className="inline-flex items-center justify-center w-11 h-11 -mr-1 text-primary-foreground/70 hover:text-primary-foreground"
          onClick={() => {
            sessionStorage.setItem("sticky-cta-dismissed", "1");
            setDismissed(true);
          }}
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default StickyMobileCTA;
